import { resolveDayOff } from '@/domain/artistAdmin';
import type { HeatmapCell } from '@/domain/queueEngine';
import type { ArtistAvailability } from '@/domain/types';

export type AvailabilityCellKind = 'working' | 'rdo' | 'comp' | 'leave';

export interface AvailabilityCellState {
  kind: AvailabilityCellKind;
  freeHours: number;
  workingHours: number;
  /** rdo / comp / leave → not bookable. */
  off: boolean;
  /** Day-off note, if any (comp / leave). */
  note?: string;
}

/**
 * The shared state of one availability-grid cell: whether the artist is working
 * (with free / working hours) or off, and if off, which kind (RDO / comp / leave).
 * Both the queue heatmap and the full-screen availability grid derive their cells
 * from this, so day-offs — including comp and leave — render consistently and
 * can't drift between the two views.
 */
export function availabilityCellState(
  availability: ArtistAvailability,
  cell: HeatmapCell,
): AvailabilityCellState {
  const dayOff = resolveDayOff(availability, new Date(cell.dayIso));
  const kind: AvailabilityCellKind = dayOff
    ? dayOff.kind
    : cell.workingHours === 0
      ? 'rdo'
      : 'working';
  return {
    kind,
    freeHours: cell.freeHours,
    workingHours: cell.workingHours,
    off: kind !== 'working',
    note: dayOff?.note,
  };
}

/**
 * Traffic-light colour scale from the agent's POV: a fully-booked artist is
 * HEALTHY (green); a wide-open day is an URGENT gap to fill (red). Shared by
 * every availability grid so the scale can't drift between views.
 */
export function intensityClass(freeHours: number, working: number): string {
  if (working === 0) return 'bg-ink-black/60 text-cream-faint/30';
  if (freeHours === 0) return 'bg-mint/55 text-ink-black';
  if (freeHours <= 2) return 'bg-gold/45 text-ink-black';
  if (freeHours <= 4) return 'bg-gold/65 text-ink-black';
  return 'bg-blood/55 text-cream';
}

/**
 * Ad-hoc absences that get a distinct letter marker in the availability grids.
 * Routine RDOs are intentionally NOT marked (they recur weekly and would clutter);
 * they render as a quiet "off" cell instead. Colours are shared so comp/leave read
 * the same in every availability view.
 */
export const DAY_OFF_MARKER: Record<'comp' | 'leave', { letter: string; label: string; cls: string }> = {
  comp: { letter: 'C', label: 'Comp day', cls: 'bg-azure/45 text-ink-black' },
  leave: { letter: 'L', label: 'Leave', cls: 'bg-gold-dim/55 text-ink-black' },
};
